// -----------------------------------------------------------------------------
// AppelOffrePublie.jsx – cartes responsive (auto-fit) + vue détails
// -----------------------------------------------------------------------------
import { useEffect, useState, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Typography, Chip, Box, Button,
  IconButton, Alert, Divider, Dialog, Card, CardActionArea
} from '@mui/material';
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

import { fetchAllAppelOffres }  from '../../redux/appeloffreSlice';
import { fetchAllCandidatures } from '../../redux/candidatureSlice';
import { fetchProfilesOfCurrentUser } from '../../redux/profilesSlice';

import AddCandidatureForm from '../Candidatures/AddCandidatureForm';
import AppelOffreDetails  from './AppelOffreDetails';
import CustomLoader from '../Common/CustomLoader';

import './styles/AppelOffrePublie.css';

/* ------------------------------------------------------------------ */
/*  Raccourcis utilitaires                                             */
/* ------------------------------------------------------------------ */
const isExpired = d => d && dayjs(d, 'YYYY-MM-DD').isBefore(dayjs(), 'day');
const SK_BG = '#ece8ff';
const SK_TX = '#6a4cff';
const PRIMARY = '#2F4E8B';

/* ------------------------------------------------------------------ */
/*  Grid responsive (auto-fit)                                         */
/*  – défini hors composant pour garder la même identité               */
/* ------------------------------------------------------------------ */
const CardsGrid = memo(({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 5,
      // minmax(300px) : la carte garde lisibilité ; auto-fit = wrap dynamique
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(auto-fit, minmax(300px, 1fr))',
      },
      alignItems: 'stretch',
    }}
  >
    {children}
  </Box>
));

export default function AppelOffrePublie() {
  /* ---------------------- Redux ---------------------- */
  const dispatch = useDispatch();
  const { appelOffres, loading, error } = useSelector(s => s.appelOffres);
  const { currentUser }                = useSelector(s => s.users);
  const { currentUserProfiles = [] }   = useSelector(s => s.profiles);

  /* --------------------- local state ----------------- */
  const [candidatures, setCandidatures] = useState([]);
  const [page, setPage]                 = useState(1);
  const PER = 6;

  const [selected, setSelected]   = useState(null);
  const [openApply, setOpenApply] = useState(false);
  const [applyId, setApplyId]     = useState(null);
  const [openNoProf, setNoProf]   = useState(false);

  // Add ref for scrolling
  const containerRef = useRef(null);

  // Scroll to top helper
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  /* ---------------------- effects -------------------- */
  useEffect(() => {
    dispatch(fetchAllAppelOffres());
    dispatch(fetchAllCandidatures()).then(res => {
      if (Array.isArray(res.payload.candidatures)) {
        setCandidatures(res.payload.candidatures);
      }
    });
    if (currentUser?.roles.includes('ROLE_USER')) {
      dispatch(fetchProfilesOfCurrentUser());
    }
  }, [dispatch, currentUser]);

  // Scroll to top when page changes
  useEffect(() => {
    scrollToTop();
  }, [page]);

  /* ---------------------- dérivées ------------------- */
  const published   = appelOffres.filter(o => o.mode === 'publié');
  const pages       = Math.ceil(published.length / PER) || 1;
  const slice       = published.slice((page - 1) * PER, page * PER);
  const hasProfile  = currentUser?.roles.includes('ROLE_USER') && currentUserProfiles.length > 0;
  const userApplied = id =>
    candidatures.some(c => c.appelOffre.id === id && c.user.id === currentUser.id);

  /* ---------------------- handlers ------------------- */
  const openDetails  = o  => setSelected(o);
  const closeDetails = () => setSelected(null);

  const handleApply = id => {
    if (!hasProfile) return setNoProf(true);
    setApplyId(id);
    setOpenApply(true);
  };

  /* ---------------------- UI ------------------------- */
  if (loading) {
    return (
      <Container
        className="appeloffre-container-offre"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CustomLoader />
      </Container>
    );
  }
  if (error) {
    return (
      <Container className="appeloffre-container-offre">
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Box className="appeloffre-container-offre" ref={containerRef}>
      {/* ---------- En-tête ---------- */}
      <Box
        sx={{
          bgcolor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.1)',
          px: 2,
          py: 1.5,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {selected && (
          <IconButton size="small" onClick={closeDetails}>
            <ArrowBackIosIcon />
          </IconButton>
        )}
        <Typography variant="subtitle1" fontWeight={700}>
          Appels d&apos;offre
        </Typography>
      </Box>

      {/* ---------- Vue listes / détail ---------- */}
      {!selected ? (
        <>
          {published.length === 0 && (
            <Typography variant="body1" className="appeloffre-no-offres-message-offre">
              Aucun appel d&apos;offre disponible pour le moment.
            </Typography>
          )}

          {/* ====== GRID RESPONSIVE ====== */}
          <CardsGrid>
            {slice.map(offre => {
              const expired = isExpired(offre.date_cloture_offre);
              return (
                <Card
                  key={offre.id}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'border-color .2s, box-shadow .2s, transform .2s',
                    '&:hover': {
                      borderColor: '#000',
                      boxShadow: '0 4px 14px rgba(0,0,0,.15)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardActionArea
                    sx={{
                      px: 4,
                      py: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onClick={() => openDetails(offre)}
                  >
                    {/* Chips statut / tarif */}
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Chip
                        label={expired ? 'Expiré' : 'Ouvert'}
                        size="small"
                        sx={{
                          bgcolor: expired ? '#ff6b6b' : '#37d079',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Chip
                        label={offre.tjm ? `${offre.tjm} € /j` : 'Tarif N/A'}
                        size="small"
                        sx={{
                          bgcolor: SK_BG,
                          color: SK_TX,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>

                    {/* Titre */}
                    <Box textAlign="center" mb={3}>
                      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {offre.titre}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Métadonnées alignées */}
                    {[
                      ['Lieu', offre.lieu_prestation || 'Non spécifié'],
                      ['Date publication', offre.date_publication],
                      [
                        'Date clôture',
                        <Typography
                          component="span"
                          fontWeight={600}
                          sx={{ color: expired ? 'error.main' : 'success.main' }}
                        >
                          {offre.date_cloture_offre}
                        </Typography>,
                      ],
                      ['Référence', offre.reference || 'N/A'],
                      ['Réf. client', offre.reference_client || 'N/A'],
                    ].map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '110px 1fr',
                          alignItems: 'flex-start',
                          fontSize: 14,
                          py: 1,
                        }}
                      >
                        <Typography color="text.secondary">{label}</Typography>
                        <Typography fontWeight={600} sx={{ textAlign: 'right' }}>
                          {value}
                        </Typography>
                        <Divider light sx={{ gridColumn: '1 / -1', my: 0.5 }} />
                      </Box>
                    ))}
                  </CardActionArea>
                </Card>
              );
            })}
          </CardsGrid>

          {/* ====== Pagination ====== */}
          {published.length > PER && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
              <IconButton
                onClick={() => setPage(p => p - 1)}
                disabled={page === 1}
              >
                <ArrowBackIosIcon sx={{ color: PRIMARY }} />
              </IconButton>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: PRIMARY,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                {page}
              </Box>
              <IconButton
                onClick={() => setPage(p => p + 1)}
                disabled={page === pages}
              >
                <ArrowForwardIosIcon sx={{ color: PRIMARY }} />
              </IconButton>
            </Box>
          )}
        </>
      ) : (
        /* ====== Vue détail ====== */
        <AppelOffreDetails
          offre={selected}
          canApply={currentUser?.roles.includes('ROLE_USER')}
          hasApplied={userApplied(selected.id)}
          onApply={() => handleApply(selected.id)}
        />
      )}

      {/* ====== Formulaire candidature ====== */}
      <Dialog open={openApply} onClose={() => setOpenApply(false)} maxWidth="sm" fullWidth>
        <AddCandidatureForm appelOffreId={applyId} handleClose={() => setOpenApply(false)} />
      </Dialog>

      {/* ====== Alerte pas de profil ====== */}
      <Dialog
        open={openNoProf}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') setNoProf(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            p: 3,
            borderRadius: 2,
            boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
            background: 'linear-gradient(135deg,#ffffff,#d1d9e6)',
          },
        }}
      >
        <Alert
          severity="info"
          sx={{
            mb: 2,
            fontSize: '1rem',
            fontWeight: 500,
            background: 'linear-gradient(135deg,#ffffff,#d1d9e6)',
            color: PRIMARY,
            '& .MuiAlert-icon': { color: PRIMARY },
          }}
        >
          Vous devez créer un profil de candidature avant de postuler.
        </Alert>
        <Box textAlign="right">
          <Button
            variant="contained"
            onClick={() => setNoProf(false)}
            sx={{
              bgcolor: PRIMARY,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0f1d47' },
            }}
          >
            Fermer
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
